import { Icon } from "@equipmentshare/ds-icon";
import { Dropdown, List, message as antMessage, Input, Spin } from "antd";
import { pipe, ReadonlyArray, Eq, Option } from "fp";

import React from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import { Company } from "~/Company";
import { gql, Graph } from "~/Graph";

import { Order } from "~/Order";
import { AntOverride } from "~/Toast";
import * as Typography from "~/Typography";

export namespace DefectType {
  type DefectType = {
    readonly id: string;
    readonly assetTypeID: string;
    readonly name: string;
    readonly companyID: number | string | undefined;
  };

  export type Classification = "vehicle" | "trailer";

  export type ClassifiedDefectTypes = {
    readonly classification: Classification;
    readonly defectTypes: readonly DefectType[];
  };

  const Sort = Order.fromCompare<DefectType>((a, b) =>
    Order.string().compare(
      a.name.toLocaleLowerCase(),
      b.name.toLocaleLowerCase()
    )
  );

  const Equals = Eq.fromEquals<DefectType>(
    (l, r) => l.assetTypeID === r.assetTypeID && l.id === r.id
  );

  export namespace Request {
    const document = () => gql`
      ${Graph.Errors.Fragment.document()}

      query GetDefectTags($companyID: Int!) {
        assetDefectTypes {
          all(input: { companyID: $companyID }) {
            ... on Error {
              ...Error
            }

            ... on Asset__Defect__Type {
              id
              name
              company {
                id
              }
              assetType {
                id
                name
              }
            }
          }
        }
      }
    `;

    export const get = (companyID: number) => {
      const { data, loading, error: apolloError, refetch } = Graph.useQuery<
        Graph.Generated.Query
      >(document(), {
        variables: { companyID },
      });

      const [defectTypes, graphError] = React.useMemo(() => {
        if (
          data?.assetDefectTypes?.all &&
          "message" in data.assetDefectTypes.all
        ) {
          return [
            undefined,
            // @ts-ignore
            Graph.Errors.Fragment.decode(data.assetDefectTypes.all),
          ];
        }

        const decoded = pipe(
          data?.assetDefectTypes?.all || [],
          ReadonlyArray.filter(
            (dt) => dt?.__typename === "Asset__Defect__Type"
          ),
          (dt) => dt as readonly Graph.Generated.Asset__Defect__Type[],
          ReadonlyArray.map<Graph.Generated.Asset__Defect__Type, DefectType>(
            (dt) => ({
              id: dt.id,
              assetTypeID: dt.assetType.id,
              name: dt.name,
              companyID: dt.company?.id ? `${dt.company?.id}` : undefined,
            })
          )
        );

        return [decoded, undefined];
      }, [data, apolloError]);

      return {
        defectTypes: defectTypes ?? [],
        loading,
        refetch,
        apolloError,
        graphError,
      };
    };

    export const addTags = (
      names: readonly string[],
      assetTypeID: number,
      companyID: number,
      onCompleted: () => void
    ) =>
      Graph.useMutation<Graph.Generated.Mutation>(
        gql`
          ${Graph.Errors.Fragment.document()}

          mutation AddTags(
            $names: [String!]!
            $assetTypeID: Int!
            $companyID: ID!
          ) {
            AssetDefectTypeAddMany: assetDefectTypes {
              create(
                input: {
                  assetTypeID: $assetTypeID
                  names: $names
                  company: { id: $companyID }
                  lifecycle: { submitted: { actor: { actorType: USER } } }
                }
              ) {
                ... on Error {
                  ...Error
                }

                ... on Asset__Defect__Type {
                  id
                  name
                }
              }
            }
          }
        `,
        {
          variables: { names, companyID, assetTypeID },
          refetchQueries: [{ query: document(), variables: { companyID } }],
          onCompleted,
        }
      );

    export const removeTags = (
      ids: readonly string[],
      companyID: number,
      onCompleted: () => void
    ) =>
      Graph.useMutation<Graph.Generated.Mutation>(
        gql`
          ${Graph.Errors.Fragment.document()}

          mutation RemoveTags($ids: [ID!]!) {
            AssetDefectTypeAddMany: assetDefectTypes {
              delete(ids: $ids) {
                ... on Error {
                  ...Error
                }

                ... on Asset__Defect__Type {
                  id
                  name
                }
              }
            }
          }
        `,
        {
          variables: { ids },
          refetchQueries: [{ query: document(), variables: { companyID } }],
          onCompleted,
        }
      );
  }

  export const DropdownContainer = styled.div`
    .toast-message {
      background-color: white;
      border: 0;
      box-shadow: rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
        rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
        rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;
    }
  `;

  export function Editor({
    types,
    defectTypeID,
    newTypes,
    toBeRemoved,
    setNewTypes,
    setToBeRemoved,
    loading,
  }: Editor.Props) {
    const { company } = Company.use();
    const [rawFilter, setRawFilter] = React.useState("");
    const defectFilter = rawFilter.trim().toLowerCase();

    const showToastMessage = (message: string, remove = false) =>
      remove
        ? antMessage.success({
            content: message,
            className: "toast-message",
            duration: 3,
            icon: (
              <Icon
                name="$icon_delete_bin_fill"
                color="$color_primary_red"
                size="$icon_size_5"
                layout={{
                  margin: { right: "$spacing_2" },
                }}
              />
            ),
            style: { width: "460px" },
          })
        : antMessage.success({
            content: message,
            className: "toast-message",
            duration: 3,
            style: { width: "460px" },
          });

    const addedTypes = pipe(
      newTypes,
      ReadonlyArray.map((dt) => ({
        ...dt,
        onRemove: () => {
          pipe(
            newTypes,
            ReadonlyArray.filter(
              (type) => type.assetTypeID !== defectTypeID || type.id !== dt.id
            ),
            setNewTypes
          );
          void showToastMessage("Tag Removed!", true);
        },
      }))
    );
    const removedTypes = pipe(
      toBeRemoved,
      ReadonlyArray.map((dt) => ({
        ...dt,
        removed: true,
        onRemove: () => {
          pipe(
            toBeRemoved,
            ReadonlyArray.filter(
              (type) => type.assetTypeID !== defectTypeID || type.id !== dt.id
            ),
            setToBeRemoved
          );
          void showToastMessage("Tag Restored!");
        },
      }))
    );
    const baseTypes = pipe(
      types,
      ReadonlyArray.map((dt) => ({
        ...dt,
        onRemove: dt?.companyID
          ? () => {
              void showToastMessage("Tag Removed!", true);
              setToBeRemoved([...toBeRemoved, dt]);
            }
          : undefined,
      }))
    );
    const refinedList = pipe(
      [...addedTypes, ...removedTypes, ...baseTypes],
      ReadonlyArray.uniq(Equals),
      ReadonlyArray.filter(
        (dt) =>
          dt.name.trim().toLowerCase().includes(defectFilter) &&
          dt.assetTypeID === defectTypeID
      ),
      ReadonlyArray.sort(Sort)
    );

    const uniqueName =
      defectFilter !== "" &&
      pipe(
        refinedList,
        ReadonlyArray.findFirst(
          (dt) =>
            dt.assetTypeID === defectTypeID &&
            dt.name.toLowerCase() === defectFilter
        ),
        Option.isNone
      );

    const submitNewType = () => {
      pipe(
        rawFilter.trim(),
        Option.fromPredicate(() => uniqueName),
        Option.map((name) => {
          setNewTypes([
            ...newTypes,
            {
              id: name,
              assetTypeID: defectTypeID,
              name,
              companyID: parseInt(company.id, 10),
            },
          ]);
          setRawFilter("");
        })
      );
      void showToastMessage("Tag added!");
    };

    const [visible, setVisible] = React.useState(false);
    return (
      <DropdownContainer>
        <AntOverride.Component />
        <Dropdown
          align={{ offset: [0, 0] }}
          trigger={["click"]}
          visible={visible}
          onVisibleChange={setVisible}
          overlay={() => (
            <>
              <Input
                autoFocus
                placeholder="Filter defects"
                value={rawFilter}
                onChange={(e) => setRawFilter(e.target.value)}
                onPressEnter={submitNewType}
                suffix={
                  uniqueName ? (
                    <Button
                      size="small"
                      type="link"
                      onClick={submitNewType}
                      icon={
                        <Icon
                          name="$icon_add_circle_line"
                          color="$color_primary_blue"
                          size="$icon_size_4"
                        />
                      }
                    >
                      Add type
                    </Button>
                  ) : (
                    <Icon
                      name="$icon_search_line"
                      color="$color_gray_200"
                      size="$icon_size_5"
                    />
                  )
                }
              />
              <Editor.ListContainer>
                <List
                  size="small"
                  bordered
                  dataSource={[...refinedList]}
                  renderItem={(dt) => <Editor.Tag {...dt} />}
                />
              </Editor.ListContainer>
            </>
          )}
        >
          <Button block style={{ textAlign: "start", padding: "0px 11px" }}>
            <Editor.ButtonContainer>
              <>
                {baseTypes.length + addedTypes.length - removedTypes.length}{" "}
                total tags
              </>
              {loading ? (
                <Spin />
              ) : visible ? (
                <Icon
                  name="$icon_chevron_up_line"
                  color="$color_gray_100"
                  size="$icon_size_5"
                />
              ) : (
                <Icon
                  name="$icon_chevron_down_line"
                  color="$color_gray_100"
                  size="$icon_size_5"
                />
              )}
            </Editor.ButtonContainer>
          </Button>
        </Dropdown>
      </DropdownContainer>
    );
  }
  export namespace Editor {
    export type Props = {
      readonly defectTypeID: string;
      readonly types: readonly DefectType[];
      readonly newTypes: readonly DefectType[];
      readonly setNewTypes: (dt: readonly DefectType[]) => void;
      readonly toBeRemoved: readonly DefectType[];
      readonly setToBeRemoved: (dt: readonly DefectType[]) => void;
      readonly loading: boolean;
    };

    export const use = (type: Classification, reset = false) => {
      const [newTypes, setNewTypes] = React.useState<readonly DefectType[]>([]);
      const [toBeRemoved, setToBeRemoved] = React.useState<
        readonly DefectType[]
      >([]);
      const { company } = Company.use();
      const {
        defectTypes,
        loading,
        refetch,
        apolloError,
        graphError,
      } = Request.get(parseInt(company.id, 10));
      const defectTypeID = type === "vehicle" ? 2 : 3;

      React.useEffect(() => {
        if (graphError) {
          Graph.Errors.displayMessage(graphError);
        }
      }, [graphError]);

      const baseTypes = React.useMemo(
        () =>
          pipe(
            defectTypes,
            ReadonlyArray.filter((dt) => dt.assetTypeID === `${defectTypeID}`)
          ),
        [defectTypes, type]
      );

      const [addMutation, { loading: addRunning }] = Request.addTags(
        pipe(
          newTypes,
          ReadonlyArray.filter((dt) => dt.assetTypeID === `${defectTypeID}`),
          ReadonlyArray.map((dt) => dt.name)
        ),
        defectTypeID,
        parseInt(company.id, 10),
        () => setNewTypes([])
      );

      const [removeMutation, { loading: removeRunning }] = Request.removeTags(
        pipe(
          toBeRemoved,
          ReadonlyArray.filter((dt) => dt.assetTypeID === `${defectTypeID}`),
          ReadonlyArray.map((dt) => dt.id)
        ),
        parseInt(company.id, 10),
        () => setToBeRemoved([])
      );

      if (reset) {
        setNewTypes(
          pipe(
            newTypes,
            ReadonlyArray.filter((dt) => dt.assetTypeID !== `${defectTypeID}`)
          )
        );
        setToBeRemoved(
          pipe(
            toBeRemoved,
            ReadonlyArray.filter((dt) => dt.assetTypeID !== `${defectTypeID}`)
          )
        );
      }

      return {
        component: (
          <>
            <Editor
              key={type}
              loading={loading || addRunning || removeRunning}
              defectTypeID={`${defectTypeID}`}
              types={baseTypes}
              newTypes={newTypes}
              setNewTypes={setNewTypes}
              toBeRemoved={toBeRemoved}
              setToBeRemoved={setToBeRemoved}
            />
            {apolloError
              ? Graph.Errors.renderApolloError(
                  "Something went wrong fetching Asset Defect Types.",
                  apolloError
                )
              : null}
          </>
        ),
        save: () => {
          newTypes.length > 0 && addMutation();
          toBeRemoved.length > 0 && removeMutation();
        },
        newTypes: newTypes.length,
        removedTypes: toBeRemoved.length,
        changed: () => newTypes.length > 0 || toBeRemoved.length > 0,
        refetch,
      } as const;
    };

    export type TagProps = DefectType & {
      readonly removed?: boolean;
      readonly onRemove?: () => void;
    };
    export const Tag = ({ name, removed, onRemove }: TagProps) => {
      const body = removed ? <s>{name}</s> : <>{name}</>;
      const removeBtn = onRemove ? (
        <Button
          size="small"
          type="text"
          icon={
            removed ? (
              <Icon name="$icon_add_circle_line" color="$color_gray_200" />
            ) : (
              <Icon name="$icon_close_circle_line" color="$color_gray_800" />
            )
          }
          onClick={onRemove}
        />
      ) : undefined;

      return (
        <List.Item extra={removeBtn}>
          <Typography.Component muted={removed}>{body}</Typography.Component>
        </List.Item>
      );
    };

    export const ListContainer = styled.div`
      background-color: white;
      overflow-y: scroll;
      max-height: 300px;
    `;

    export const ButtonContainer = styled.div`
      display: flex;
      justify-content: space-between;
    `;
  }
}
